/**
 * Polaroid — the visual primitive for Memory Snap. Used on:
 *
 *   • /memory-snap (capture flow preview)
 *   • /memory-wall (live grid, with per-snap rotation/tape)
 *   • download canvas exports (1080x1350)
 *   • share preview cards
 *
 * Composition: photo region (top) + white message region (bottom).
 * Handwritten message lives in the bottom white space — NOT overlaid
 * on the photo. Per spec: "written underneath the memory" not "caption".
 *
 * Subtle paper texture via layered radial-gradients (no external image).
 * Soft drop shadow + slight rotation handled by the consumer (so the
 * wall can stagger them differently). The component itself is upright.
 *
 * Props:
 *   photoUrl   — required selfie URL.
 *   message    — string (≤120 chars).
 *   penColor   — hex.
 *   seed       — style_seed for per-char variance.
 *   width      — render width in px. Aspect locked at 1080:1350 = 4:5.
 *   tape       — show top tape strip ('washi' | 'pin' | null).
 *   tapeColor  — override tape colour for theme matching.
 *   showCorners— faint corner-darken vignette (true on wall, false on capture).
 *   testId
 */

import React from 'react';
import HandwrittenText from './HandwrittenText';

export const POLAROID_RATIO = 0.8; // width / height — 1080×1350

// Pin & washi palettes — picked deterministically from `seed` so each
// snap reads as "pinned by a different hand". Mirrors the reference
// memory-wall aesthetic where guests use whatever pushpin / tape was
// nearest to them. Tones are muted-but-warm so the wall stays
// nostalgic rather than reading like a craft-store haul.
const PIN_PALETTE = [
  '#D14B4B', // brick red
  '#3B6AB3', // ink blue
  '#E8B53A', // mustard yellow
  '#4F8F58', // sage green
  '#C95FA0', // dusty pink
  '#5BA6B0', // teal
  '#8C5BA0', // muted plum
  '#D97A2C', // burnt orange
];
const WASHI_PALETTE = [
  'rgba(212, 175, 94, 0.55)',  // warm gold (default)
  'rgba(186, 138, 96, 0.55)',  // kraft brown
  'rgba(168, 184, 144, 0.55)', // sage
  'rgba(202, 144, 156, 0.55)', // dusty rose
  'rgba(160, 168, 192, 0.50)', // dusk blue
  'rgba(208, 188, 144, 0.55)', // cream
];

const pickFromPalette = (seed, palette) => {
  const s = Math.abs(Number(seed) || 1);
  return palette[s % palette.length];
};

export const Polaroid = ({
  photoUrl,
  message = '',
  penColor = '#1A1A1A',
  seed = 1,
  width = 320,
  tape = null,
  tapeColor,             // optional override — leave undefined for per-seed variance
  showCorners = false,
  className = '',
  testId = 'polaroid',
}) => {
  const height = width / POLAROID_RATIO;
  const photoHeight = height * 0.74;
  const messageSize = Math.max(18, Math.min(28, width * 0.072));
  // Pick the per-snap pin/washi colour deterministically from seed
  // so the same snap always looks the same on every device but the
  // wall as a whole feels varied.
  const resolvedColor = tapeColor
    ?? (tape === 'pin' ? pickFromPalette(seed, PIN_PALETTE)
    :  tape === 'washi' ? pickFromPalette(seed + 7, WASHI_PALETTE)
    :  'rgba(212, 175, 94, 0.55)');

  // Subconscious paper-stock variance — three grain treatments rotated
  // deterministically by seed. Visibility ~3% so the wall reads as
  // "premium printed stock" rather than "scrapbook texture pack":
  // each polaroid feels milled from a slightly different batch but
  // none of them shout at you.
  const paperVariant = Math.abs(Number(seed) || 1) % 3;
  const paperOverlays = [
    // 0 — warm cream (default)
    `
      radial-gradient(circle at 22% 18%, rgba(212,175,94,0.038), transparent 30%),
      radial-gradient(circle at 78% 82%, rgba(212,175,94,0.030), transparent 35%),
      radial-gradient(circle at 50% 50%, rgba(0,0,0,0.012), transparent 70%)
    `,
    // 1 — slightly cooler, finer grain (top-left bias)
    `
      radial-gradient(circle at 16% 30%, rgba(200,180,140,0.030), transparent 32%),
      radial-gradient(circle at 84% 70%, rgba(180,160,120,0.028), transparent 38%),
      radial-gradient(circle at 50% 50%, rgba(0,0,0,0.015), transparent 65%)
    `,
    // 2 — softest, almost-clean stock
    `
      radial-gradient(circle at 50% 22%, rgba(220,195,150,0.026), transparent 28%),
      radial-gradient(circle at 30% 76%, rgba(200,180,140,0.025), transparent 36%),
      radial-gradient(circle at 50% 50%, rgba(0,0,0,0.010), transparent 75%)
    `,
  ];
  const paperBase = ['#FCFCF7', '#FBF9F3', '#FCFBF6'][paperVariant];

  return (
    <div
      data-testid={testId}
      className={className}
      style={{
        position: 'relative',
        width,
        height,
        background: paperBase,
        // Layered paper texture — three deterministic grain variants
        // rotate per seed so the wall reads as "different batches of
        // printed stock" without ever showing an overlay or pattern.
        backgroundImage: paperOverlays[paperVariant],
        borderRadius: 4,
        boxShadow: '0 14px 38px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35)',
        padding: width * 0.034,
        boxSizing: 'border-box',
        userSelect: 'none',
      }}
    >
      {/* Tape — top centre. Slight rotation hardcoded; consumers can
          add page-level rotation independently. Tape colour resolved
          per-seed so each snap on the wall reads as physically
          pinned by a different hand. */}
      {tape === 'washi' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -width * 0.06,
            left: '50%',
            transform: `translateX(-50%) rotate(${(seed % 5) - 2}deg)`,
            width: width * 0.42,
            height: width * 0.085,
            background: resolvedColor,
            opacity: 0.82,
            borderRadius: 1,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            zIndex: 2,
            // Subtle parallel-line texture suggests washi tape rather
            // than flat plastic.
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.07) 0 2px, transparent 2px 6px)',
          }}
        />
      )}
      {tape === 'pin' && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -width * 0.026,
            left: `${48 + ((seed * 13) % 8)}%`,    // slight horizontal jitter
            transform: 'translateX(-50%)',
            width: width * 0.072,
            height: width * 0.072,
            background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.55) 8%, ${resolvedColor} 32%, ${resolvedColor} 70%, rgba(0,0,0,0.55) 100%)`,
            borderRadius: '50%',
            boxShadow: `0 3px 6px rgba(0,0,0,0.5), 0 0 0 1px ${resolvedColor}80, inset 0 -2px 3px rgba(0,0,0,0.35)`,
            zIndex: 3,
          }}
        />
      )}

      {/* Photo region */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: photoHeight,
          overflow: 'hidden',
          background: '#000',
          borderRadius: 2,
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            crossOrigin="anonymous"
          />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1a1a2e, #15161B)' }} />
        )}
        {showCorners && (
          // Faint vignette — feels like a physical print on a wall.
          <div
            aria-hidden
            style={{
              position: 'absolute', inset: 0,
              boxShadow: 'inset 0 0 36px rgba(0,0,0,0.25)',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* Message region — handwritten, sits below the photo. */}
      <div
        style={{
          marginTop: width * 0.034,
          paddingLeft: width * 0.024,
          paddingRight: width * 0.024,
          minHeight: height - photoHeight - (width * 0.034 * 2) - 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <HandwrittenText
          text={message}
          color={penColor}
          seed={seed}
          size={messageSize}
          align="left"
          testId={`${testId}-message`}
        />
      </div>
    </div>
  );
};

export default Polaroid;
